import type { HTMLAttributes, ReactNode } from 'react';
import { TickCircle } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import styles from './Stepper.module.css';

export type StepStatus = 'complete' | 'current' | 'upcoming';

export interface StepItem {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  /** Overrides the status derived from `currentIndex`. */
  status?: StepStatus;
}

export interface StepperProps extends HTMLAttributes<HTMLOListElement> {
  steps: StepItem[];
  /** Zero-based index of the current step. */
  currentIndex: number;
  orientation?: 'horizontal' | 'vertical';
  /** Accessible name of the step list. */
  label?: string;
}

const STATUS_TEXT: Record<StepStatus, string> = {
  complete: 'Completed',
  current: 'Current step',
  upcoming: 'Not started',
};

export function Stepper({
  steps,
  currentIndex,
  orientation = 'horizontal',
  label = 'Progress',
  className,
  ...rest
}: StepperProps) {
  return (
    <ol aria-label={label} className={cx(styles.stepper, styles[orientation], className)} {...rest}>
      {steps.map((step, index) => {
        const status: StepStatus =
          step.status ??
          (index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming');
        return (
          <li
            key={step.id}
            className={cx(styles.step, styles[status])}
            aria-current={status === 'current' ? 'step' : undefined}
          >
            <span className={styles.marker} aria-hidden="true">
              {status === 'complete' ? (
                <TickCircle size={20} color="currentColor" variant="Bold" />
              ) : (
                index + 1
              )}
            </span>
            <span className={styles.text}>
              <span className={styles.label}>
                <span className="sr-only">
                  Step {index + 1} of {steps.length}, {STATUS_TEXT[status]}:{' '}
                </span>
                {step.label}
              </span>
              {step.description && <span className={styles.description}>{step.description}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
